import React from "react";
import { MdEdit } from "react-icons/md";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PaziaBox, PaziaLoader } from "components/atoms";
import { TanTableClient, TanTableColumn } from "components/molecules";
import { courseRegistrationApi, RegistrationRevenueSummaryApi } from "api/registrations";
import { RetainedRevenueModal } from "components/templates/RetainedRevenue/RetainedRevenueModal";
import { retainedRevenueChoiceFromAmount } from "components/templates/RetainedRevenue/RetainedRevenueFields";
import { routes } from "consts";
import { QUERIES_KEYS } from "queries";
import { formatDate, toMoneyFormat, useToaster } from "utils";

const removedRevenueQueryKey = (courseId: number) => ["removedRegistrationsRevenue", courseId];

/** "Usunięci kursanci" — what removed students paid and how much of it stays in the course revenue (editable). */
export const RemovedStudentsRevenue = ({ courseId }: { courseId: number }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToaster();
  const [editItem, setEditItem] = React.useState<RegistrationRevenueSummaryApi | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: removedRevenueQueryKey(courseId),
    queryFn: () => courseRegistrationApi.getRemovedRevenue(courseId),
  });

  const columns: TanTableColumn<RegistrationRevenueSummaryApi>[] = [
    {
      id: "student",
      header: "Kursant",
      accessorKey: "surname",
      cell: (row) => (
        <Link className="text-primary-600 hover:underline" to={routes.STUDENT(row.row.original.userId)}>
          {row.row.original.firstName} {row.row.original.surname}
        </Link>
      ),
    },
    {
      id: "deletedAt",
      header: "Usunięto",
      accessorKey: "deletedAt",
      cell: (row) => (row.getValue<string | null>() ? formatDate(new Date(row.getValue<string>())) : "—"),
    },
    {
      id: "paidAmount",
      header: "Wpłacono",
      accessorKey: "paidAmount",
      cell: (row) => toMoneyFormat(row.getValue<number>()),
    },
    {
      id: "advanceAmount",
      header: "W tym zaliczka",
      accessorKey: "advanceAmount",
      cell: (row) => toMoneyFormat(row.getValue<number>()),
    },
    {
      id: "retainedRevenue",
      header: "Zatrzymany przychód",
      accessorKey: "retainedRevenue",
      cell: (row) => <span className="font-semibold">{toMoneyFormat(row.getValue<number>())}</span>,
    },
    {
      id: "actions",
      header: "Akcje",
      accessorKey: "registrationId",
      cell: (row) => (
        <MdEdit
          size={24}
          title="Zmień zatrzymany przychód"
          className="cursor-pointer hover:text-blue-500"
          onClick={(e) => {
            e.stopPropagation();
            setEditItem(row.row.original);
          }}
        />
      ),
    },
  ];

  const removed = data ?? [];

  return (
    <PaziaBox>
      <h2 className="mb-1 text-lg font-semibold text-slate-800 dark:text-slate-100">Usunięci kursanci</h2>
      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
        Wpłaty kursantów usuniętych z kursu nie są liczone do przychodu. Do przychodu wlicza się tylko zatrzymana kwota
        (np. zaliczka).
      </p>
      {isLoading ? (
        <PaziaLoader />
      ) : removed.length === 0 ? (
        <div className="text-sm text-slate-500 dark:text-slate-400">Brak usuniętych kursantów.</div>
      ) : (
        <TanTableClient data={removed} columns={columns} />
      )}
      <RetainedRevenueModal
        id="edit-retained-revenue"
        isOpen={!!editItem}
        title={`Zatrzymany przychód — ${editItem?.firstName ?? ""} ${editItem?.surname ?? ""}`}
        confirmText="Zapisz"
        amounts={editItem ?? undefined}
        initialChoice={
          editItem
            ? retainedRevenueChoiceFromAmount(editItem.retainedRevenue, editItem.paidAmount, editItem.advanceAmount)
            : undefined
        }
        onCloseRequest={() => setEditItem(null)}
        onConfirm={async (choice) => {
          if (!editItem) return;
          try {
            await courseRegistrationApi.updateRetainedRevenue(courseId, editItem.registrationId, choice);
            showSuccess("Zapisano zatrzymany przychód");
            setEditItem(null);
            queryClient.invalidateQueries({ queryKey: removedRevenueQueryKey(courseId) });
            queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.COURSE_FINANCES(courseId) });
            queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.COURSE_FINANCES_BY_CATEGORIES(courseId) });
          } catch (e) {
            showError("Nie udało się zapisać zatrzymanego przychodu.");
            console.error(e);
          }
        }}
      />
    </PaziaBox>
  );
};
